import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HomePins, HomeTabs } from '@local/client-contracts';
import { Constants, performanceCheckpoint } from '@local/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterService } from '@shared/services/router.service';
import { cloneDeep, isEqual } from 'lodash';
import { filter, map } from 'rxjs/operators';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { SETTINGS_WIDGET, WidgetsService } from '../../services/widgets.service';
import { PostWidgetService } from 'src/app/bar/views/home-page/services/post-widget.service';
import { EventsService } from '@shared/services';
import { GridChanges, GridLayoutSettings, GridModel, GridWidgetSettings } from '../../widgets/grid/grid.component';
import { HomeComponentFocused, HomePageService } from 'src/app/bar/services/home-page.service';
import { HeaderWidgetService } from '../../services/header-widget.service';
import { AddToCollectionSelect } from 'src/app/bar/components/selected-item-popup/models/add-to-collection-select';

export interface EmptyStateItem {
  id: string;
  icon: string;
  title: string;
  disabledWorkspaceFlag?: string;
}

@UntilDestroy()
@Component({
  selector: 'home-tab',
  templateUrl: './home-tab.component.html',
  styleUrls: ['./home-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeTabComponent implements OnInit {
  private readonly COMPONENT_FOCUSED_ID = HomeComponentFocused.WIDGETS;
  private readonly MIN_WIDTH_SAVE_STATE: number = 770;
  private _tab: HomeTabs.HomeTab;
  private _homePins: HomePins.HomePin[];
  private currentWidgets: HomeTabs.Widget[];
  dashboardReady = false;
  dashboard: GridModel;
  emptyStateItems: EmptyStateItem[];

  @Input() componentFocused: HomeComponentFocused = HomeComponentFocused.SEARCH_INPUT;
  @Input() isPopupOpen = false;
  @Input() set homePins(val: HomePins.HomePin[]) {
    this._homePins = val;
    this.initDashboard();
  }
  @Input() ownerOrAdmin: boolean;
  @Input() set tab(val: HomeTabs.HomeTab) {
    if (isEqual(this._tab, val)) {
      return;
    }
    const tabChanged = this.tab?.id !== val?.id;
    this._tab = cloneDeep(val);
    if (this.tab) {
      this.eventsService.event('home_tabs.switch', {
        label: this.tab.name,
        location: { title: 'home' },
      });
    }
    this.initDashboard(tabChanged);
  }

  get homePins(): HomePins.HomePin[] {
    return this._homePins;
  }

  get tab(): HomeTabs.HomeTab {
    return this._tab;
  }

  get DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG(): string {
    return Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG;
  }

  @Output() removeFocusSearchInput: EventEmitter<void> = new EventEmitter<void>();
  @Output() scrollToWidget: EventEmitter<HomeTabs.Widget> = new EventEmitter<HomeTabs.Widget>();

  constructor(
    private cdr: ChangeDetectorRef,
    public widgetsService: WidgetsService,
    private routerService: RouterService,
    private postWidgetService: PostWidgetService,
    private collectionsService: CollectionsService,
    private eventsService: EventsService,
    private homePageService: HomePageService,
    private headerWidgetService: HeaderWidgetService
  ) {
    this.emptyStateItems = [
      {
        id: 'collection',
        icon: 'icon-collection',
        title: 'Select a collection',
        disabledWorkspaceFlag: Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG,
      },
      { id: 'post', icon: 'icon-plus', title: 'Add post' },
      { id: 'header', icon: 'icon-header', title: 'Add header' },
    ];
  }

  ngOnInit(): void {
    this.routerService.queryParams$
      .pipe(
        untilDestroyed(this),
        filter((params) => params['widget']),
        map((p) => p['widget'])
      )
      .subscribe(async (p) => {
        if (!this.tab) {
          return;
        }
        const currWidget = this.tab?.widgets.find((w) => (w as HomeTabs.CollectionWidget).collectionId === p);
        this.scrollToWidget.emit(currWidget);
        this.routerService.removeQueryParam('widget');
      });
    this.homePageService.triggerComponentFocus$.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.live === this.COMPONENT_FOCUSED_ID && !this.dashboard?.settings?.length) {
        this.homePageService.keyboardComponentFocus$.next({ step: value.step, live: this.COMPONENT_FOCUSED_ID });
      }
    });
  }

  widgetsChanged($event: GridChanges) {
    for (const settings of $event) {
      const index = this.currentWidgets?.findIndex((w) => w.id === settings.id);
      if (index === -1) {
        continue;
      }
      this.currentWidgets[index].viewSetting = {
        ...this.currentWidgets[index].viewSetting,
        x: settings.x,
        y: settings.y,
        cols: settings.w,
        rows: settings.h,
      };
    }
    this.widgetsService.submitChangeEvent($event);
    this.widgetsService.update(this.tab.id, this.currentWidgets);
  }

  widgetsResized($event: GridLayoutSettings) {
    this.widgetsService.submitResizeEvent($event);
  }

  private initDashboard(tabChanged: boolean = false) {
    if (!this.tab) {
      this.dashboard = null;
      return;
    }
    this.currentWidgets = cloneDeep(this.tab.widgets.filter((w) => !w.hide));
    const newDash: HomeTabs.Widget[] = this.currentWidgets;
    const oldWidgets: GridWidgetSettings[] = this.dashboard?.settings;
    const newWidgets: GridWidgetSettings[] = newDash?.map((d) => ({ id: d.id, ...this.widgetsService.getViewSettings(d) }));

    if (tabChanged || !this.dashboard) {
      this.dashboard = {
        id: this.tab.id,
        settings: newWidgets,
        minRow: SETTINGS_WIDGET.minRows,
        cellHeight: SETTINGS_WIDGET.cellHeight,
        minSaveWidth: this.MIN_WIDTH_SAVE_STATE,
        column: SETTINGS_WIDGET.maxCols,
      };
    } else if (!isEqual(oldWidgets, newWidgets)) {
      const widgets: GridWidgetSettings[] = [];
      for (const widget of newDash || []) {
        const newSettings = this.widgetsService.getViewSettings(widget);
        const oldWidget = this.dashboard?.settings?.find((o) => o.id === widget.id);
        if (oldWidget) {
          Object.entries(newSettings).forEach(([key, value]) => (oldWidget[key] = value));
        }
        widgets.push(oldWidget || newSettings);
      }
      this.dashboard = { ...this.dashboard, settings: widgets };
    }
    this.widgetsService.setWidgetSettings(newDash);

    if (!this.dashboardReady) {
      performanceCheckpoint('time-to-dashboard-ready');
      this.dashboardReady = true;
    }
    this.cdr.markForCheck();
  }

  focusWidgetChanged(id: string) {
    const currentWidget = this.currentWidgets.find((w) => w.id === id);
    this.widgetsService.triggerWidgetFocus$.next(id);
    this.scrollToWidget.emit(currentWidget);
  }

  addItemToTab(itemType: string) {
    switch (itemType) {
      case 'collection':
        this.collectionsService.openSelectPopup$.next({
          data: {
            title: `Add Collection to "${this.tab.name}" Tab`,
            itemsList: [],
            tabId: this.tab?.id,
            collections: this.collectionsService.collections,
            currentUser: this.collectionsService.currentUser,
          } as AddToCollectionSelect,
          type: 'add-collection-to-tab',
        });
        break;
      case 'post':
        this.postWidgetService.openNewPostPopup({ tab: this.tab });
        break;
      case 'header':
        this.headerWidgetService.createHeaderWidget(this.tab.id);
        break;
      default:
        break;
    }
  }
}
